import React, { useState, useEffect } from 'react';
import { useI18n } from '@/contexts/I18nContext';
import { useConnectionSuggestions, ConnectionSuggestion } from '@/features/home/hooks/useConnectionSuggestions';
import { FramedAvatar } from '@/components/ui/FramedAvatar';
import { PremiumBadge } from '@/components/ui/PremiumBadge';
import { Plus, Users, Briefcase, Star, MapPin } from 'lucide-react';
import { connectionsRepository } from '@/features/home/repositories/supabaseConnectionsRepository';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/shared/hooks/use-toast';
import { useLocation, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { EmptyStateCard } from '@/components/ui/EmptyStateCard';
import { getInitials } from '@/utils/avatarUtils';
import { useMySignals } from '@/features/sossial/hooks/useMySignals';
import { SingleStepWizardModal, WizardStepType } from '@/components/sossial/SingleStepWizardModal';
import { EditLocationModal } from '@/features/profile/components/EditLocationModal';

const ConnectCommunitySection: React.FC = () => {
  const { t } = useI18n();
  const { user } = useAuth();
  const { data: suggestions, isLoading, error, refetch } = useConnectionSuggestions();
  const [pendingConnections, setPendingConnections] = useState<Set<string>>(new Set());
  const [wizardType, setWizardType] = useState<WizardStepType | null>(null);
  const [locationOpen, setLocationOpen] = useState(false);
  const [signalsRefreshKey, setSignalsRefreshKey] = useState(0);
  const { signals } = useMySignals(user?.id, signalsRefreshKey);
  const [sentConnections, setSentConnections] = useState<Set<string>>(() => {
    // Recuperar conexões enviadas do localStorage
    try {
      const saved = localStorage.getItem('sentConnections');
      return saved ? new Set(JSON.parse(saved)) : new Set();
    } catch {
      return new Set();
    }
  });
  const [showSentBadge, setShowSentBadge] = useState(() => {
    // Recuperar estado da pílula do localStorage
    try {
      const saved = localStorage.getItem('showSentBadge');
      return saved ? JSON.parse(saved) : true;
    } catch {
      return true;
    }
  });
  const { toast } = useToast();
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // Forçar recarregamento dos dados quando o usuário retorna para a página Home
  useEffect(() => {
    if (location.pathname === '/home') {
      // Invalidar queries relacionadas para forçar recarregamento
      queryClient.invalidateQueries({ queryKey: ['connection-suggestions'] });
      queryClient.invalidateQueries({ queryKey: ['next-trainings'] });
      queryClient.invalidateQueries({ queryKey: ['journey-highlights'] });
      queryClient.invalidateQueries({ queryKey: ['top-posts'] });
      
      // Forçar refetch imediato
      refetch();
    }
  }, [location.pathname, queryClient, refetch]);

  // Persistir conexões enviadas no localStorage
  const updateSentConnections = (newConnections: Set<string>) => {
    setSentConnections(newConnections);
    try {
      localStorage.setItem('sentConnections', JSON.stringify([...newConnections]));
    } catch (error) {
      console.error('Error saving sent connections to localStorage:', error);
    }
  };

  // Persistir estado da pílula no localStorage
  const updateShowSentBadge = (show: boolean) => {
    setShowSentBadge(show);
    try {
      localStorage.setItem('showSentBadge', JSON.stringify(show));
    } catch (error) {
      console.error('Error saving showSentBadge to localStorage:', error);
    }
  };

  const handleConnect = async (userId: string) => {
    if (!user) {
      toast({
        title: t('common.error'),
        description: t('home.connect_community.login_required'),
        variant: "destructive",
      });
      return;
    }

    try {
      // Adicionar à lista de pendentes imediatamente
      setPendingConnections(prev => new Set(prev).add(userId));
      
      await connectionsRepository.sendConnectionRequest(user.id, userId);

      // Adicionar à lista de enviadas
      const newSentConnections = new Set(sentConnections).add(userId);
      updateSentConnections(newSentConnections);

      // Remover da lista de pendentes
      setPendingConnections(prev => {
        const newPending = new Set(prev);
        newPending.delete(userId);
        return newPending;
      });

      // Mostrar feedback de sucesso
      toast({
        title: t('home.connect_community.connection_sent'),
        description: t('home.connect_community.connection_sent_description'),
      });

      // Atualizar badge
      if (showSentBadge) {
        updateShowSentBadge(false);
      }

    } catch (error) {
      console.error('Error sending connection request:', error);
      
      // Remover da lista de pendentes em caso de erro
      setPendingConnections(prev => {
        const newPending = new Set(prev);
        newPending.delete(userId);
        return newPending;
      });

      toast({
        title: t('common.error'),
        description: t('home.connect_community.connection_error'),
        variant: "destructive",
      });
    }
  };

  const handleSignalSaved = () => {
    setSignalsRefreshKey(k => k + 1);
    queryClient.invalidateQueries({ queryKey: ['connection-suggestions'] });
  };

  const handleLocationSave = async (data: { country: string; state: string; city: string }) => {
    if (!user) return;
    await connectionsRepository.updateLocation(user.id, data);
    setLocationOpen(false);
    handleSignalSaved();
  };

  const handleRefresh = async () => {
    try {
      await refetch();
      
      // Mostrar feedback de sucesso
      toast({
        title: t('home.connect_community.refreshed'),
        description: t('home.connect_community.refreshed_description'),
      });
    } catch (error) {
      console.error('Error refreshing suggestions:', error);
      toast({
        title: t('common.error'),
        description: t('home.connect_community.refresh_error'),
        variant: "destructive",
      });
    }
  };

  // Navegar para o perfil do usuário
  const handleUserClick = (username: string) => {
    navigate(`/profile/${username}`, {
      state: { 
        fromHome: true
      }
    });
  };

  // Filtrar sugestões para não mostrar usuários com conexões já enviadas
  const filteredSuggestions = suggestions?.filter(suggestion => 
    !sentConnections.has(suggestion.id)
  ) || [];

  const hasSuggestions = filteredSuggestions.length > 0;
  const hasAnySuggestions = suggestions && suggestions.length > 0;
  const allSuggestionsSent = hasAnySuggestions && !hasSuggestions && sentConnections.size > 0;

  if (isLoading) {
    return (
      <div className="mb-8">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold text-white">Connect with the Community</h2>
          <button
            onClick={handleRefresh}
            className="text-sm text-white underline font-roboto font-normal"
          >
            Refresh
          </button>
        </div>
        <div className="relative overflow-hidden" style={{ maxWidth: 'calc(100vw - 20px)' }}>
          <div className="w-full overflow-x-auto pb-2 scrollbar-hide">
            <div className="flex space-x-4">
              {[1, 2, 3, 4, 5].map((i) => (
                <div key={i} className="flex-shrink-0 w-48 bg-[rgb(45,36,36)] rounded-lg p-4 animate-pulse">
                  <div className="flex flex-col items-center mb-3">
                    <div className="w-[42px] h-[48px] bg-gray-700 rounded-full mb-3"></div>
                    <div className="h-4 bg-gray-700 rounded mb-2"></div>
                    <div className="h-3 bg-gray-700 rounded mb-3"></div>
                    <div className="h-4 bg-gray-700 rounded"></div>
                  </div>
                </div>
              ))}
              
              {/* Botão Add new training como card separado */}
              <div 
                className="flex-shrink-0 w-48 bg-[rgb(45,36,36)] rounded-lg p-4 border border-white cursor-pointer hover:bg-[rgb(55,46,46)] transition-colors"
                onClick={() => navigate('/calendar', { state: { openAddTrainingModal: true } })}
              >
                <div className="flex flex-col items-center justify-center h-full">
                  <Plus className="w-8 h-8 text-white mb-3" />
                  <span className="text-white text-center font-roboto text-sm">Add new training</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="mb-8">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold text-white">Connect with the Community</h2>
          <button
            onClick={handleRefresh}
            className="text-sm text-white underline font-roboto font-normal"
          >
            Refresh
          </button>
        </div>
        <div className="text-red-400 text-center py-8">
          Error loading suggestions. Please try again.
        </div>
      </div>
    );
  }

  return (
    <div className="mb-8">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center space-x-2">
          <h2 className="text-sm font-bold text-white">{t('home.connect_community.title')}</h2>
          {showSentBadge && sentConnections.size > 0 && (
            <span className="text-xs text-gray-400 bg-gray-700 px-2 py-1 rounded-full">
              {t('home.connect_community.sent_badge', { count: sentConnections.size })}
            </span>
          )}
        </div>
        <button
          onClick={handleRefresh}
          className="text-xs text-white underline font-roboto font-normal hover:text-gray-300 transition-colors"
        >
          {t('home.connect_community.refresh')}
        </button>
      </div>

      {!hasSuggestions ? (
        allSuggestionsSent ? (
          <EmptyStateCard
            title={t('home.connect_community.all_sent')}
            description={t('home.connect_community.all_sent_description')}
            buttonText={t('home.connect_community.refresh')}
            onButtonClick={handleRefresh}
          />
        ) : (
          // No suggestions yet — show targeted CTAs for each missing signal
          <div className="space-y-2">
            {signals && !signals.main_academy_id && (
              <button
                onClick={() => setWizardType('academy')}
                className="w-full text-left bg-[rgb(45,36,36)] rounded-lg px-4 py-3 flex items-center gap-3 active:opacity-80"
              >
                <Users className="w-4 h-4 text-sossiety-red flex-shrink-0" />
                <span className="text-[12px] text-white/80">{t('discovery.cta.add_academy_title')}</span>
              </button>
            )}
            {signals && !signals.profession && (
              <button
                onClick={() => setWizardType('profession')}
                className="w-full text-left bg-[rgb(45,36,36)] rounded-lg px-4 py-3 flex items-center gap-3 active:opacity-80"
              >
                <Briefcase className="w-4 h-4 text-sossiety-red flex-shrink-0" />
                <span className="text-[12px] text-white/80">{t('discovery.cta.add_profession_title')}</span>
              </button>
            )}
            {signals && !signals.has_interests && (
              <button
                onClick={() => setWizardType('interests')}
                className="w-full text-left bg-[rgb(45,36,36)] rounded-lg px-4 py-3 flex items-center gap-3 active:opacity-80"
              >
                <Star className="w-4 h-4 text-sossiety-red flex-shrink-0" />
                <span className="text-[12px] text-white/80">{t('discovery.cta.pick_interests_title')}</span>
              </button>
            )}
            {signals && !signals.city && !signals.country && (
              <button
                onClick={() => setLocationOpen(true)}
                className="w-full text-left bg-[rgb(45,36,36)] rounded-lg px-4 py-3 flex items-center gap-3 active:opacity-80"
              >
                <MapPin className="w-4 h-4 text-sossiety-red flex-shrink-0" />
                <span className="text-[12px] text-white/80">{t('discovery.cta.add_location_title')}</span>
              </button>
            )}
            {(!signals || (signals.main_academy_id && signals.profession && signals.has_interests && (signals.city || signals.country))) && (
              <EmptyStateCard
                title={t('home.connect_community.no_suggestions')}
                description={t('home.connect_community.no_suggestions_description')}
                buttonText={t('home.connect_community.refresh')}
                onButtonClick={handleRefresh}
              />
            )}
          </div>
        )
      ) : (
        <div className="relative overflow-hidden" style={{ maxWidth: 'calc(100vw - 20px)' }}>
          <div className="w-full overflow-x-auto pb-2 scrollbar-hide">
            <div className="flex space-x-4">
              {filteredSuggestions.map((suggestion) => {
                const isPending = pendingConnections.has(suggestion.id);
                
                return (
                  <div key={suggestion.id} className="flex-shrink-0 flex flex-col justify-between w-48 bg-[rgb(45,36,36)] rounded-lg p-4">
                    <div 
                      className="flex flex-col items-center mb-3 cursor-pointer hover:opacity-80 transition-opacity"
                      onClick={() => handleUserClick(suggestion.username)}
                    >
                      <FramedAvatar
                        src={suggestion.profile_picture_url}
                        alt={suggestion.full_name}
                        initials={getInitials(suggestion.full_name)}
                        frameSize={{ w: 48, h: 48 }}
                        size={28}
                        beltRank={suggestion.belt_rank || 'white'}
                        className="mb-3"
                        username={suggestion.username}
                      />
                      <div className="text-center mt-2">
                        <div className="flex items-center justify-center gap-1">
                          <span className="font-normal text-white text-[12px] font-roboto truncate max-w-[80px]">
                            {suggestion.full_name}
                          </span>
                          {suggestion.is_premium && <PremiumBadge size="xs" />}
                        </div>
                        {suggestion.academy_name && (
                          <div className="text-[10px] text-gray-300 font-roboto">
                            {suggestion.academy_name}
                            {suggestion.academy_city && `, ${suggestion.academy_city}`}
                          </div>
                        )}
                      </div>
                    </div>
                    
                    <button
                      onClick={(e) => {
                        e.stopPropagation(); // Evitar que o clique do botão propague para o container
                        handleConnect(suggestion.id);
                      }}
                      disabled={isPending}
                      className={`w-full py-1 rounded-full text-[10px] font-medium transition-colors ${
                        isPending
                          ? 'bg-gray-600 text-gray-300 cursor-not-allowed'
                          : 'bg-red-600 hover:bg-red-700 text-white'
                      }`}
                    >
                      {isPending ? t('home.connect_community.sending') : t('home.connect_community.connect')}
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Signal wizards */}
      <SingleStepWizardModal
        open={wizardType !== null}
        type={wizardType}
        onClose={() => setWizardType(null)}
        onSaved={handleSignalSaved}
      />
      <EditLocationModal
        open={locationOpen}
        onClose={() => setLocationOpen(false)}
        onSave={handleLocationSave}
        initialData={{ country: '', state: '', city: '' }}
      />
    </div>
  );
};

export default ConnectCommunitySection;
